"use client";

import { c as _c } from "react/compiler-runtime";
import { ReactNode, useCallback } from "react";
import { AuthSurface } from "@/components/auth";
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui";
import { cx } from "@/lib/tailwind-utils";
import { SelfSchedulePatient } from "@/types/self-schedule";
import { MfaAuth, MfaAuthPanel, MfaPhoneAuthPanel } from "./mfa-auth";
export interface MfaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Patient-facing surface used for the `patient_auth_resolved` event. */
  surface: AuthSurface;
  tAndSFooterText: string;
  title?: string;
  description?: string;
  onPatientVerified?: (patient: SelfSchedulePatient) => void;
}

/** Dialog shell for patient-facing auth prompts. */
function MfaDialogContent(t0) {
  const $ = _c(11);
  const {
    title,
    maxWidthClassName,
    children
  } = t0;
  let t1;
  if ($[0] !== maxWidthClassName) {
    t1 = cx("transition-[max-width] duration-200", maxWidthClassName);
    $[0] = maxWidthClassName;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== title) {
    t2 = <DialogHeader><DialogTitle className="sr-only">{title}</DialogTitle></DialogHeader>;
    $[2] = title;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== children) {
    t3 = <DialogBody className="w-full">{children}</DialogBody>;
    $[4] = children;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <DialogFooter />;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t1 || $[8] !== t2 || $[9] !== t3) {
    t5 = <DialogContent className={t1}>{t2}{t3}{t4}</DialogContent>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}
function MfaDialogShell(t0) {
  const $ = _c(7);
  const {
    title,
    description,
    tAndSFooterText
  } = t0;
  let t1;
  if ($[0] !== description || $[1] !== tAndSFooterText || $[2] !== title) {
    t1 = <MfaAuthPanel title={title} description={description} tAndSFooterText={tAndSFooterText} />;
    $[0] = description;
    $[1] = tAndSFooterText;
    $[2] = title;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t1 || $[5] !== title) {
    t2 = <MfaDialogContent title={title} maxWidthClassName="sm:max-w-md">{t1}</MfaDialogContent>;
    $[4] = t1;
    $[5] = title;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
function MfaPhoneDialogShell(t0) {
  const $ = _c(7);
  const {
    title,
    description,
    tAndSFooterText
  } = t0;
  let t1;
  if ($[0] !== description || $[1] !== tAndSFooterText || $[2] !== title) {
    t1 = <MfaPhoneAuthPanel title={title} description={description} tAndSFooterText={tAndSFooterText} />;
    $[0] = description;
    $[1] = tAndSFooterText;
    $[2] = title;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t1 || $[5] !== title) {
    t2 = <MfaDialogContent title={title} maxWidthClassName="sm:max-w-md">{t1}</MfaDialogContent>;
    $[4] = t1;
    $[5] = title;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
export function MfaDialog(t0) {
  const $ = _c(14);
  const {
    open,
    onOpenChange,
    surface,
    title: t1,
    description,
    tAndSFooterText,
    onPatientVerified
  } = t0;
  const title = t1 === undefined ? "Verify Your Identity" : t1;
  let t2;
  if ($[0] !== onOpenChange || $[1] !== onPatientVerified) {
    t2 = patient => {
      onOpenChange(false);
      onPatientVerified?.(patient);
    };
    $[0] = onOpenChange;
    $[1] = onPatientVerified;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const onResolved = t2;
  let t3;
  if ($[3] !== description || $[4] !== onResolved || $[5] !== open || $[6] !== surface || $[7] !== tAndSFooterText || $[8] !== title) {
    t3 = open && <MfaAuth surface={surface} onPatientVerified={onResolved}><MfaDialogShell title={title} description={description} tAndSFooterText={tAndSFooterText} /></MfaAuth>;
    $[3] = description;
    $[4] = onResolved;
    $[5] = open;
    $[6] = surface;
    $[7] = tAndSFooterText;
    $[8] = title;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== onOpenChange || $[11] !== open || $[12] !== t3) {
    t4 = <Dialog open={open} onOpenChange={onOpenChange}>{t3}</Dialog>;
    $[10] = onOpenChange;
    $[11] = open;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
}
export function MfaPhoneDialog(t0) {
  const $ = _c(14);
  const {
    open,
    onOpenChange,
    surface,
    title: t1,
    description,
    tAndSFooterText,
    onPatientVerified
  } = t0;
  const title = t1 === undefined ? "Verify Your Identity" : t1;
  let t2;
  if ($[0] !== onOpenChange || $[1] !== onPatientVerified) {
    t2 = patient => {
      onOpenChange(false);
      onPatientVerified?.(patient);
    };
    $[0] = onOpenChange;
    $[1] = onPatientVerified;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const onResolved = t2;
  let t3;
  if ($[3] !== description || $[4] !== onResolved || $[5] !== open || $[6] !== surface || $[7] !== tAndSFooterText || $[8] !== title) {
    t3 = open && <MfaAuth surface={surface} onPatientVerified={onResolved} allowPortalAccountHolders={true}><MfaPhoneDialogShell title={title} description={description} tAndSFooterText={tAndSFooterText} /></MfaAuth>;
    $[3] = description;
    $[4] = onResolved;
    $[5] = open;
    $[6] = surface;
    $[7] = tAndSFooterText;
    $[8] = title;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== onOpenChange || $[11] !== open || $[12] !== t3) {
    t4 = <Dialog open={open} onOpenChange={onOpenChange}>{t3}</Dialog>;
    $[10] = onOpenChange;
    $[11] = open;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
}